import React from 'react';
import { performanceMonitor } from '../utils/performance';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    console.error('Error caught by boundary:', error, errorInfo);
    
    // Record the error in performance monitor
    performanceMonitor.recordEvent('react_error', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    // Update state with error details
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Report error to backend if needed
    this.reportError(error, errorInfo);
  }

  reportError = async (error, errorInfo) => {
    try {
      // You could send error reports to your backend here
      const errorReport = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      };
      
      console.log('Error report:', errorReport);
      // await fetch('/api/error-report', { method: 'POST', body: JSON.stringify(errorReport) });
    } catch (reportError) {
      console.error('Failed to report error:', reportError);
    }
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      const errorBoundaryStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        fontFamily: 'Arial, sans-serif'
      };

      const errorContainerStyle = {
        background: 'white',
        padding: '2rem',
        borderRadius: '10px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        textAlign: 'center',
        maxWidth: '500px',
        margin: '1rem'
      };

      const h2Style = {
        color: '#333',
        marginBottom: '1rem',
        fontSize: '1.5rem'
      };

      const pStyle = {
        color: '#666',
        marginBottom: '2rem',
        lineHeight: '1.5'
      };

      const errorActionsStyle = {
        display: 'flex',
        gap: '1rem',
        justifyContent: 'center',
        marginBottom: '1rem'
      };

      const buttonBaseStyle = {
        padding: '0.75rem 1.5rem',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'all 0.3s ease'
      };

      const retryButtonStyle = {
        ...buttonBaseStyle,
        background: '#4CAF50',
        color: 'white'
      };

      const reloadButtonStyle = {
        ...buttonBaseStyle,
        background: '#2196F3',
        color: 'white'
      };

      const errorDetailsStyle = {
        marginTop: '1rem',
        textAlign: 'left'
      };

      const summaryStyle = {
        cursor: 'pointer',
        padding: '0.5rem',
        background: '#f5f5f5',
        borderRadius: '3px'
      };

      const preStyle = {
        background: '#f8f8f8',
        padding: '1rem',
        borderRadius: '3px',
        overflowX: 'auto',
        fontSize: '0.8rem',
        margin: '0.5rem 0'
      };

      // Fallback UI
      return (
        <div style={errorBoundaryStyle}>
          <div style={errorContainerStyle}>
            <h2 style={h2Style}>🎲 Oops! Something went wrong</h2>
            <p style={pStyle}>The Ludo game encountered an unexpected error. Don't worry, your game data is safe!</p>
            
            <div style={errorActionsStyle}>
              <button 
                onClick={this.handleRetry} 
                style={retryButtonStyle}
                onMouseEnter={(e) => e.target.style.background = '#45a049'}
                onMouseLeave={(e) => e.target.style.background = '#4CAF50'}
              >
                Try Again
              </button>
              <button 
                onClick={() => window.location.reload()} 
                style={reloadButtonStyle}
                onMouseEnter={(e) => e.target.style.background = '#1976D2'}
                onMouseLeave={(e) => e.target.style.background = '#2196F3'}
              >
                Reload Game
              </button>
            </div>
            
            {process.env.NODE_ENV === 'development' && (
              <details style={errorDetailsStyle}>
                <summary style={summaryStyle}>Error Details (Development)</summary>
                <pre style={preStyle}>{this.state.error && this.state.error.toString()}</pre>
                <pre style={preStyle}>{this.state.errorInfo.componentStack}</pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
